import {
  AssignmentInd,
  Cancel,
  Download,
  EventAvailable,
  InsertDriveFile,
  TaskAlt,
  Update,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  getOriginalDocumentFile,
  getSignedDocumentFile,
} from 'lib/api/integrations/digitalSignature';
import InfoCard from 'lib/common/GeneralComponents/TenderModal/Components/InfoCard';
import SectionCard from 'lib/common/GeneralComponents/TenderModal/Components/SectionCard';
import { formatDate } from 'lib/functions/utils';
import { Portfolio, SignDocument, SignOnDocument } from 'lib/types/models';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

const formatDateString = (value?: string | null): string => {
  if (!value) return 'No disponible';
  try {
    return formatDate(new Date(value), 'dd-mm-yyyy');
  } catch {
    return 'No disponible';
  }
};

const signerChip = (sd: SignOnDocument, idx: number) => {
  const label = `${sd.providerIdentifier}`;
  const color = sd.signedAt ? 'success' : sd.rejectedAt ? 'error' : 'warning';
  const icon = sd.signedAt ? (
    <TaskAlt sx={{ fontSize: 16 }} />
  ) : sd.rejectedAt ? (
    <Cancel sx={{ fontSize: 16 }} />
  ) : (
    <AssignmentInd sx={{ fontSize: 16 }} />
  );
  const tooltip = sd.signedAt
    ? `ID ${sd.providerIdentifier} firmó el documento el ${formatDateString(sd.signedAt)}`
    : sd.rejectedAt
      ? `ID ${sd.providerIdentifier} rechazó el documento el ${formatDateString(sd.rejectedAt)}`
      : `ID ${sd.providerIdentifier} está pendiente de firma`;

  return (
    <Tooltip key={`${sd.providerIdentifier}-${idx}`} title={tooltip} arrow>
      <Chip
        icon={icon}
        label={label}
        size="small"
        color={color}
        variant={sd.signedAt || sd.rejectedAt ? 'filled' : 'outlined'}
        sx={{
          fontWeight: sd.signedAt ? 600 : 'normal',
          '& .MuiChip-label': {
            fontSize: '0.75rem',
          },
        }}
      />
    </Tooltip>
  );
};

const DocumentCard = ({ document }: { document: SignDocument }) => {
  const dispatch = useDispatch();
  const [loadingOriginal, setLoadingOriginal] = useState(false);
  const [originalFileUrl, setOriginalFileUrl] = useState<string | null>(null);
  const [loadingSigned, setLoadingSigned] = useState(false);
  const [signedFileUrl, setSignedFileUrl] = useState<string | null>(null);

  const fetchOriginalDocument = async () => {
    try {
      setLoadingOriginal(true);
      const response = await getOriginalDocumentFile(dispatch, document.id);
      if (response) {
        const url = URL.createObjectURL(response.blob);
        setOriginalFileUrl(url);
      }
    } catch (error) {
      console.error('Error fetching original document:', error);
    } finally {
      setLoadingOriginal(false);
    }
  };

  const fetchSignedDocument = async () => {
    try {
      setLoadingSigned(true);
      const response = await getSignedDocumentFile(dispatch, document.id);
      if (response) {
        const url = URL.createObjectURL(response.blob);
        setSignedFileUrl(url);
      }
    } catch (error) {
      console.error('Error fetching signed document:', error);
    } finally {
      setLoadingSigned(false);
    }
  };

  const handleOriginalFileClick = () => {
    if (originalFileUrl) window.open(originalFileUrl, '_blank');
  };

  const handleSignedFileClick = () => {
    if (signedFileUrl) window.open(signedFileUrl, '_blank');
  };

  useEffect(() => {
    void fetchOriginalDocument();
    void fetchSignedDocument();
  }, [document.id]);

  useEffect(() => {
    return () => {
      if (originalFileUrl) URL.revokeObjectURL(originalFileUrl);
      if (signedFileUrl) URL.revokeObjectURL(signedFileUrl);
    };
  }, [originalFileUrl, signedFileUrl]);

  return (
    <Card
      key={document.id}
      variant="outlined"
      sx={{
        borderRadius: 1.5,
        borderColor: 'divider',
        backgroundColor: 'grey.50',
      }}
    >
      <CardHeader
        avatar={<InsertDriveFile color="primary" sx={{ fontSize: 18 }} />}
        title={
          <Typography
            variant="caption"
            fontWeight={600}
            color="text.primary"
            sx={{ fontSize: '0.75rem' }}
          >
            {document.description ?? 'Documento'}
          </Typography>
        }
        sx={{ py: 0.75, px: 1 }}
      />
      <CardContent sx={{ pt: 0.25, pb: 1, px: 1 }}>
        <Grid container spacing={0.75}>
          <InfoCard
            icon={<AssignmentInd />}
            label="Firma"
            valueText={document.signature}
            color={'primary.main'}
          />
          <InfoCard
            icon={<TaskAlt />}
            label="Estado"
            valueText={document.status}
            color={'info.main'}
          />
          <InfoCard
            icon={<EventAvailable />}
            label="Creado"
            valueText={formatDateString(document.createdAt)}
            color={'success.main'}
          />
          <InfoCard
            icon={<Update />}
            label="Actualizado"
            valueText={formatDateString(document.updatedAt)}
            color={'warning.main'}
          />

          <Grid item xs={12} my={1}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {loadingOriginal ? (
                <Skeleton
                  variant="rectangular"
                  height={35}
                  width={120}
                  sx={{ borderRadius: 1.25 }}
                />
              ) : originalFileUrl ? (
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 1.25,
                    px: 1,
                    py: 0.75,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    cursor: 'pointer',
                    backgroundColor: 'background.paper',
                    '&:hover': { boxShadow: 1 },
                  }}
                  onClick={handleOriginalFileClick}
                >
                  <Download sx={{ fontSize: 16, color: 'primary.main' }} />
                  <Typography variant="caption" color="text.primary">
                    Archivo original
                  </Typography>
                </Card>
              ) : null}
              {loadingSigned ? (
                <Skeleton
                  variant="rectangular"
                  height={35}
                  width={120}
                  sx={{ borderRadius: 1.25 }}
                />
              ) : signedFileUrl ? (
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 1.25,
                    px: 1,
                    py: 0.75,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    cursor: 'pointer',
                    backgroundColor: 'background.paper',
                    '&:hover': { boxShadow: 1 },
                  }}
                  onClick={handleSignedFileClick}
                >
                  <Download sx={{ fontSize: 16, color: 'success.main' }} />
                  <Typography variant="caption" color="text.primary">
                    Archivo firmado
                  </Typography>
                </Card>
              ) : null}
            </Box>
          </Grid>

          <Grid item xs={12}>
            {document.signers && document.signers.length > 0 ? (
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {document.signers.map(signerChip)}
                </Stack>
              </Stack>
            ) : (
              <Typography variant="caption" color="text.secondary">
                No se han realizado firmas en este documento
              </Typography>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const Documents: React.FC<{ portfolio: Portfolio }> = ({ portfolio }) => (
  <SectionCard title="Documentos" chipLabel="Asociados a este portafolio">
    {portfolio.documents && portfolio.documents.length > 0 ? (
      <Stack divider={<Divider flexItem />} spacing={1.25}>
        {portfolio.documents.map((document: SignDocument) => (
          <DocumentCard key={document.id} document={document} />
        ))}
      </Stack>
    ) : (
      <Typography variant="caption">No hay documentos disponibles</Typography>
    )}
  </SectionCard>
);

export default Documents;
